// File System Access import + drag-drop + paste parsing.

export const supportsDirectoryPicker = () =>
  typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const MAX_WALK = 2000;

function extOf(name) {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(i) : "";
}

function makeItem(name, path, type, depth) {
  const parent = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
  return {
    id: uid(),
    name,
    path,
    parent,
    type,
    depth,
    ext: type === "file" ? extOf(name) : "",
  };
}

async function walk(dirHandle, prefix, depth, items) {
  const entries = [];
  for await (const [name, handle] of dirHandle.entries()) entries.push([name, handle]);
  entries.sort((a, b) => {
    const ad = a[1].kind === "directory" ? 0 : 1;
    const bd = b[1].kind === "directory" ? 0 : 1;
    return ad - bd || a[0].localeCompare(b[0]);
  });
  for (const [name, handle] of entries) {
    if (items.length >= MAX_WALK) return;
    const path = `${prefix}/${name}`;
    if (handle.kind === "directory") {
      items.push({ ...makeItem(name, path, "folder", depth), _handle: handle });
      await walk(handle, path, depth + 1, items);
    } else {
      items.push({ ...makeItem(name, path, "file", depth), _handle: handle });
    }
  }
}

export async function pickDirectory() {
  if (!supportsDirectoryPicker()) throw new Error("unsupported");
  const dir = await window.showDirectoryPicker({ mode: "readwrite" }).catch(() => window.showDirectoryPicker());
  return readDirHandle(dir);
}

export const supportsFilePicker = () =>
  typeof window !== "undefined" && typeof window.showOpenFilePicker === "function";

// Individual multi-file picker (any type, no extension whitelist).
export async function pickFiles() {
  if (!supportsFilePicker()) throw new Error("unsupported");
  const handles = await window.showOpenFilePicker({ multiple: true });
  const rootId = uid();
  return handles.map((h) => ({ ...makeItem(h.name, h.name, "file", 0), rootId, _handle: h }));
}

// Recursively read dropped files/folders (additive). Each folder gets its own root.
export async function readDropItems(dataTransfer) {
  const out = [];
  const its = [...(dataTransfer.items || [])].filter((i) => i.kind === "file" && i.getAsFileSystemHandle);
  for (const it of its) {
    try {
      const h = await it.getAsFileSystemHandle();
      if (h.kind === "directory") {
        const res = await readDirHandle(h);
        out.push(...res.items);
      } else {
        const rootId = uid();
        out.push({ ...makeItem(h.name, h.name, "file", 0), rootId, _handle: h });
      }
    } catch { /* skip */ }
  }
  return out;
}

export async function readDirHandle(dir) {
  const rootId = uid();
  const root = { ...makeItem(dir.name, dir.name, "folder", 0), _handle: dir };
  root.id = "root-folder-" + rootId;
  root.isRoot = true;
  const items = [root];
  await walk(dir, dir.name, 1, items);
  items.forEach((i) => { i.rootId = rootId; });
  return { dirName: dir.name, items, truncated: items.length >= MAX_WALK };
}

// Parse pasted lines. "/" expresses nesting; a trailing "/" marks a folder.
export function parsePastedList(text) {
  const items = [];
  const seenFolders = new Set();
  text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .forEach((line) => {
      const isFolder = line.endsWith("/");
      const path = line.replace(/\/+$/, "");
      const segments = path.split("/").filter(Boolean);
      const name = segments[segments.length - 1] || path;
      const type = isFolder ? "folder" : "file";
      const key = `${type}:${path}`;
      if (seenFolders.has(key)) return;
      seenFolders.add(key);
      items.push(makeItem(name, path, type, segments.length - 1));
    });
  return items;
}

export { downloadText } from "@/lib/rename/script";
